import { useToast as useChakraToast } from '@chakra-ui/react';
import { Dispatch, SetStateAction } from 'react';
import { getNameExtension } from 'utils/files';
import { useToast } from 'utils/hooks';
import { MetricsTableData } from '..';
import {
  useGenerateNumericAnswers,
  useGenerateNarrativeAnswers,
  useReadPdfText,
} from './AnswersGenerator.hooks';
import { GeneratedAnswer } from './MetricsAITypes';
import { useTranslation } from 'utils/translation';
import { Attachment } from 'models';
import * as Excel from 'exceljs';
import { Button } from 'Atoms';
import { AssessableMetrics } from '../Metrics';
import mixpanel from 'mixpanel-browser';
import { TRACKING_EVENTS } from 'utils/mixpanel';
import { TimePeriods } from '../../Requirement';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';

const ALLOWED_FILE_TYPES = ['pdf', 'xlsx'];

const mergeMetricAnswers = (allAnswers: GeneratedAnswer[]): GeneratedAnswer[] => {
  const bestMap = new Map<string, GeneratedAnswer>();

  allAnswers.forEach((ans) => {
    const key = ans.metricTitleWithTags ?? ans.metricTitle;
    const existing = bestMap.get(key);

    const isValid = ans.answer !== '-' && ans.answer !== 'NA';

    if (!existing) {
      bestMap.set(key, ans);
      return;
    }

    const existingValid = existing.answer !== '-' && existing.answer !== 'NA';

    if (!existingValid && isValid) {
      bestMap.set(key, ans);
    } else if (existingValid && isValid) {
      if ((ans.confidence ?? 0) > (existing.confidence ?? 0)) {
        // If the current answer is better than the existing one, replace it based on confidence
        bestMap.set(key, ans);
      }
    }
  });

  return Array.from(bestMap.values());
};

const readExcelFile = async (fileUrl: string, fileName: string): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      console.error(`Error fetching Excel file: ${response.status} - ${response.statusText}`);
      reject("Couldn't read excel file");
    }

    const blob = await response.blob();
    const xFile = new File([blob], fileName ?? '');

    const fileReader = new FileReader();

    fileReader.readAsArrayBuffer(xFile);

    fileReader.onload = async (e) => {
      const bufferArray = e.target?.result;

      if (bufferArray) {
        const workbook = new Excel.Workbook();
        await workbook.xlsx.load(bufferArray as ArrayBuffer);

        const worksheet = workbook.worksheets[0];

        const sheetValues = worksheet.getSheetValues();
        const excelData = sheetValues
          .filter((value) => value?.length ?? 0 > 0)
          .map((row) => {
            if (Array.isArray(row)) {
              const filteredRow = row.filter((v) => !!v);
              return filteredRow.join(': ');
            }
          });

        const excelDataString = excelData.join(', ');

        resolve(excelDataString);
      } else {
        reject("Couldn't read excel file");
      }
    };

    fileReader.onerror = (error) => {
      console.error(error);
      reject("Couldn't read excel file");
    };
  });
};

export const AnswersGeneratorButton = ({
  isDisabled,
  setGeneratedAnswers,
  numericMetrics,
  narrativeMetrics,
  abortControllerRef,
  attachments,
  isGeneratingAnswers,
  setIsGeneratingAnswers,
  initializeAbortController,
  selectedQuarter,
}: {
  isDisabled: boolean;
  setGeneratedAnswers: Dispatch<SetStateAction<GeneratedAnswer[]>>;
  numericMetrics: MetricsTableData[];
  narrativeMetrics: AssessableMetrics;
  abortControllerRef: React.RefObject<AbortController | null>;
  attachments?: Attachment[];
  isGeneratingAnswers: boolean;
  setIsGeneratingAnswers: Dispatch<SetStateAction<boolean>>;
  initializeAbortController: () => void;
  selectedQuarter: TimePeriods;
}) => {
  const nhost = useNhostClient();
  const { t } = useTranslation('ai');
  const toast = useToast();
  const chakraToast = useChakraToast();

  const { generateNumericAnswers } = useGenerateNumericAnswers({
    quantitativeMetrics: numericMetrics,
    abortControllerRef,
    selectedQuarter,
  });

  const { generateManyNarrativeAnswers } = useGenerateNarrativeAnswers({
    narrativeMetrics,
    abortControllerRef,
  });

  const { readPdfText } = useReadPdfText({
    abortControllerRef,
  });

  const extractAndGenerateAnswers = async () => {
    if (!attachments || attachments.length === 0) {
      return toast({
        text: t('esrs.errors.noAttachments'),
        variant: 'danger',
      });
    }

    const allAnswers: GeneratedAnswer[] = [];
    const toastId = toast({
      text: t('esrs.answers.progress'),
      closable: true,
      duration: null,
    });

    try {
      setIsGeneratingAnswers(true);

      for (const attachment of attachments) {
        const file = attachment?.file;
        const fileName = file?.title ?? '';
        const fileType = getNameExtension(file?.storageFile?.name ?? '').extension;

        if (!ALLOWED_FILE_TYPES.includes(fileType)) {
          toast({
            text: t('fileRestrictions'),
            variant: 'danger',
          });
          continue;
        }

        const preSignedUrlResult = await nhost.storage.getFilePresignedURL(file?.storageFile?.id);
        const fileUrl = preSignedUrlResult.body.url ?? '';
        const isExcelFile = fileType === 'xlsx';

        const getText = async () => {
          if (isExcelFile) {
            const excelResult = await readExcelFile(fileUrl, fileName);
            return excelResult;
          } else {
            const pdfResult = await readPdfText(fileUrl, fileName);
            return pdfResult;
          }
        };

        const result = await getText();

        if (result) {
          const quantitativeAnswers = await generateNumericAnswers(result);
          const narrativeAnswers = await generateManyNarrativeAnswers(result);

          allAnswers.push(
            ...[...quantitativeAnswers, ...narrativeAnswers]
              .filter((answer) => !!answer)
              .map((answer) => ({
                ...answer,
                status: null,
                sourceFileId: file?.id ?? '',
                sourceFileName: fileName,
              }))
          );
        } else {
          toast({
            variant: 'danger',
            text: t('esrs.errors.readFile'),
          });
        }
      }

      // Merge answers to clear duplicates and keep the first valid answer for numeric metrics, and the most confident answer for narrative metrics
      const mergedAnswers = mergeMetricAnswers(allAnswers);

      setGeneratedAnswers(mergedAnswers);
      setIsGeneratingAnswers(false);
      chakraToast.close(toastId);
    } catch (error) {
      setIsGeneratingAnswers(false);
      setGeneratedAnswers([]);
      chakraToast.close(toastId);

      console.error('Error during answer generation', error);
      toast({
        variant: 'danger',
        text: t('esrs.errors.general'),
      });
    }
  };

  const handleGenerate = async () => {
    mixpanel.track(TRACKING_EVENTS.ESRS.AI.GENERATION_STARTED);
    initializeAbortController();
    await extractAndGenerateAnswers();
  };

  return (
    <Button
      onClick={handleGenerate}
      isDisabled={isDisabled}
      variant="primary"
      size="md"
      isLoading={isGeneratingAnswers}
    >
      Run the AI
    </Button>
  );
};
